import "./agenticPageSkeleton.css";
import PropTypes from "prop-types";

function boneWidthForColumn(index, total) {
  if (index === 0) return "58%";
  if (index === total - 1) return "42%";
  if (index % 4 === 0) return "72%";
  if (index % 3 === 0) return "48%";
  return "64%";
}

function getVariantClass(variant) {
  if (variant === "circle") {
    return "aps-bone--circle";
  }

  if (variant === "pill") {
    return "aps-bone--pill";
  }

  return "";
}

export function SkeletonBone({ className = "", style, variant }) {
  const variantClass = getVariantClass(variant);
  return (
    <span
      className={`aps-bone ${variantClass} ${className}`.trim()}
      style={style}
      aria-hidden
    />
  );
}

SkeletonBone.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  variant: PropTypes.string,
};

export function KpiCardsSkeleton({ count = 4 }) {
  return (
    <div className="aps-kpi-grid" aria-hidden>
      {Array.from({ length: count }, (_, index) => (
        <div key={`kpi-skel-${index}`} className="aps-kpi-card">
          <div className="aps-kpi-card-inner">
            <div className="aps-kpi-main">
              <SkeletonBone style={{ height: 10, width: "46%" }} />
              <SkeletonBone style={{ height: 24, width: "38%" }} />
              <div className="aps-kpi-breakdown">
                {Array.from({ length: 3 }, (_, bi) => (
                  <div key={bi} className="aps-kpi-breakdown-item">
                    <SkeletonBone style={{ height: 8, width: 44 }} />
                    <SkeletonBone style={{ height: 12, width: 28 }} />
                  </div>
                ))}
              </div>
            </div>
            <SkeletonBone variant="circle" style={{ width: 34, height: 34 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

KpiCardsSkeleton.propTypes = {
  count: PropTypes.number,
};

export function FilterBarSkeleton({ count = 5, showActions = true, inline = false }) {
  return (
    <div
      className={`aps-filter-bar${inline ? " aps-filter-bar--inline" : ""}`}
      aria-hidden
    >
      {Array.from({ length: count }, (_, index) => (
        <div
          key={`filter-skel-${index}`}
          className={`aps-filter-item${inline ? " aps-filter-item--inline" : ""}`}
        >
          <SkeletonBone style={{ height: 10, width: "62%" }} />
          <SkeletonBone style={{ height: 30, width: "100%" }} />
        </div>
      ))}
      {showActions ? (
        <div className="aps-filter-actions">
          <SkeletonBone variant="circle" style={{ width: 28, height: 28 }} />
          <SkeletonBone variant="circle" style={{ width: 28, height: 28 }} />
          <SkeletonBone style={{ height: 30, width: 108 }} />
        </div>
      ) : null}
    </div>
  );
}

FilterBarSkeleton.propTypes = {
  count: PropTypes.number,
  showActions: PropTypes.bool,
  inline: PropTypes.bool,
};

export function TableHeadSkeleton({ columns = 10, className = "" }) {
  return (
    <div
      className={`aps-table-head-skel ${className}`.trim()}
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(72px, 1fr))` }}
      aria-hidden
    >
      {Array.from({ length: columns }, (_, index) => (
        <SkeletonBone
          key={`head-skel-${index}`}
          className="aps-table-head-cell"
          style={{ width: index % 2 === 0 ? "70%" : "55%" }}
        />
      ))}
    </div>
  );
}

TableHeadSkeleton.propTypes = {
  columns: PropTypes.number,
  className: PropTypes.string,
};

export function TableBodySkeletonRows({
  rows = 10,
  columns = 10,
  rowClassName = "aps-table-skel-row",
  cellClassName = "aps-table-skel-cell",
  boneHeight = 12,
}) {
  const safeRows = Math.max(1, Number(rows) || 1);
  const safeColumns = Math.max(1, Number(columns) || 1);

  return Array.from({ length: safeRows }, (_, rowIndex) => (
    <tr key={`table-skel-row-${rowIndex}`} className={rowClassName}>
      {Array.from({ length: safeColumns }, (_, colIndex) => (
        <td key={`table-skel-cell-${rowIndex}-${colIndex}`} className={cellClassName}>
          <SkeletonBone
            style={{
              height: boneHeight,
              width: boneWidthForColumn(colIndex, safeColumns),
            }}
          />
        </td>
      ))}
    </tr>
  ));
}

export function PaginationSkeleton() {
  return (
    <div className="aps-pagination" aria-hidden>
      <div className="aps-pagination-left">
        <SkeletonBone style={{ height: 12, width: 88 }} />
        <SkeletonBone style={{ height: 28, width: 52 }} />
        <SkeletonBone style={{ height: 12, width: 72 }} />
      </div>
      <div className="aps-pagination-right">
        <SkeletonBone variant="circle" style={{ width: 28, height: 28 }} />
        <SkeletonBone style={{ height: 28, width: 120 }} />
        <SkeletonBone variant="circle" style={{ width: 28, height: 28 }} />
      </div>
    </div>
  );
}

/** Full Monitor Today layout while user prefs / first paint load. */
export function MonitorTodayPageSkeleton({
  tableRows = 10,
  tableColumns = 12,
  filterCount = 5,
}) {
  return (
    <output className="aps-monitor-today-skeleton" aria-live="polite" aria-busy="true">
      <div className="summary-wrapper">
        <KpiCardsSkeleton />
      </div>
      <FilterBarSkeleton count={filterCount} />
      <div className="table-agentic-wrapper">
        <div className="table-card">
          <div className="aps-table-shell">
            <TableHeadSkeleton columns={tableColumns} />
            <table className="table email-triage-dispatch-table" aria-hidden>
              <tbody>
                <TableBodySkeletonRows
                  rows={tableRows}
                  columns={tableColumns}
                  rowClassName="table-row aps-table-skel-row"
                  cellClassName="aps-table-skel-cell aps-table-skel-cell--email"
                />
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <PaginationSkeleton />
    </output>
  );
}

MonitorTodayPageSkeleton.propTypes = {
  tableRows: PropTypes.number,
  tableColumns: PropTypes.number,
  filterCount: PropTypes.number,
};

/** TnT Linkage table body rows — use inside existing `<tbody>`. */
export function TntLinkageTableSkeletonRows({ rows = 10 }) {
  return (
    <TableBodySkeletonRows
      rows={rows}
      columns={14}
      rowClassName="tnt-row aps-table-skel-row"
      cellClassName="tnt-td aps-table-skel-cell"
      boneHeight={11}
    />
  );
}

TntLinkageTableSkeletonRows.propTypes = {
  rows: PropTypes.number,
};